// ./src/App.tsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SdFlcReactRouterPages } from "@sdflc/react-router-pages";
import ReactGA from "react-ga4";
import "@/i18n";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import '@/styles/global.css';

import {
  AppProvider,
  useApp,
  GoogleMapsProvider,
  ThemeProvider,
  AppEssentialsProvider,
  UserProfileProvider,
  DashboardProvider,
  UserNotificationsProvider,
  ExpenseTagsProvider,
  SavedPlacesProvider,
  ActiveTravelProvider,
  PageVisibilityProvider,
  EntityFiltersProvider,
  LocationHistoryProvider,
  PWAProvider,
  ActiveParkingProvider,
  LayoutUIProvider,
  AppEnvironmentProvider,
  TrackingStorageProvider
} from "@/contexts";
import { LoadingOverlay, Favicon } from "@/components/generic";
import { setAuthErrorHandler, clearAuthErrorHandler } from "@/utils/api/authErrorHandler";
import { SIGNIN_PAGE } from "@/config";

import { usePageTracking } from "./hooks";
import { OnboardingGuard } from "./components/guards";

ReactGA.initialize(import.meta.env.VITE_GOOGLE_TAG);

// ============================================
// Auth error handler setup hook
// ============================================

/**
 * Sets up the global auth error handler.
 * Must be called inside router context (needs useNavigate).
 */
const useAuthErrorSetup = () => {
  const navigate = useNavigate();
  const { signOut } = useApp();

  useEffect(() => {
    setAuthErrorHandler(async (code, message) => {
      console.warn(`[Auth Error] Code: ${code}, Message: ${message}`);

      // Clear auth state
      await signOut();

      // Build redirect URL with return path
      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams();

      // Only add returnUrl if not already on auth pages
      if (currentPath && !currentPath.startsWith("/account/")) {
        searchParams.set("returnUrl", currentPath);
      }

      const redirectUrl = searchParams.toString()
        ? `${SIGNIN_PAGE}?${searchParams.toString()}`
        : SIGNIN_PAGE;

      // Navigate to sign-in
      navigate(redirectUrl, { replace: true });
    });

    // Cleanup on unmount
    return () => {
      clearAuthErrorHandler();
    };
  }, [navigate, signOut]);
};

// ============================================
// Public auth pages - no providers needed
// ============================================

const PUBLIC_AUTH_PAGES = [
  "/account/signin",
  "/account/signup",
  "/account/forgot-password",
  "/account/reset-password",
  "/account/accept-invitation",
];

const isPublicAuthPage = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_AUTH_PAGES.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  );
};

// ============================================
// Router Wrapper for public pages (minimal)
// ============================================

const PublicRouterWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <>{children}</>;
};

// ============================================
// Router Wrapper for authenticated pages (full)
// Includes all providers that need router context
// ============================================

const AuthenticatedRouterWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Set up global auth error handler
  useAuthErrorSetup();

  return (
    <PageVisibilityProvider>
      <AppEnvironmentProvider>
        <PWAProvider
          updateCheckInterval={60 * 60 * 1000}
          autoUpdate={false}
        >

          <EntityFiltersProvider>
            {/* AppEssentialsProvider loads lookup data (fuel types, expense categories, etc.) */}
            <AppEssentialsProvider>
              {/* UserProfileProvider loads user preferences */}
              <UserProfileProvider>
                {/* UserNotificationsProvider loads user notifications */}
                <UserNotificationsProvider>
                  {/* DashboardProvider replaces CarsProvider + CarStatsProvider.
                    Loads cars and their stats in a single API call. */}
                  <DashboardProvider autoLoad>
                    {/* ExpenseTagsProvider loads user's expense tags */}
                    <ExpenseTagsProvider>
                      {/* SavedPlacesProvider loads saved places as an app-level
                          read cache. Travel drawers, expense forms, and place
                          pickers consume this. The SavedPlacesPage uses its own
                          local provider and syncs back via notifyPlaceChanged/
                          notifyPlaceRemoved. */}
                      <SavedPlacesProvider autoLoad pageSize={200}>
                        <TrackingStorageProvider>
                          <ActiveTravelProvider>
                            <ActiveParkingProvider>
                              <LayoutUIProvider>
                                <OnboardingGuard>{children}</OnboardingGuard>
                              </LayoutUIProvider>
                            </ActiveParkingProvider>
                          </ActiveTravelProvider>
                        </TrackingStorageProvider>
                      </SavedPlacesProvider>
                    </ExpenseTagsProvider>
                  </DashboardProvider>
                </UserNotificationsProvider>
              </UserProfileProvider>
            </AppEssentialsProvider>
          </EntityFiltersProvider>
        </PWAProvider>
      </AppEnvironmentProvider>
    </PageVisibilityProvider>
  );
};

// ============================================
// App Content for public pages
// ============================================

const PublicAppContent: React.FC = () => {
  const { siteMap, userRoles } = useApp();

  return (
    <SdFlcReactRouterPages
      siteMap={siteMap}
      roles={userRoles}
      appWrapper={PublicRouterWrapper}
    />
  );
};

// ============================================
// App Content for authenticated pages
// ============================================

const AuthenticatedAppContent: React.FC = () => {
  const { siteMap, userRoles } = useApp();

  return (
    <SdFlcReactRouterPages
      siteMap={siteMap}
      roles={userRoles}
      appWrapper={AuthenticatedRouterWrapper}
    />
  );
};

// ============================================
// Main App with conditional provider loading
// ============================================

const AuthenticatedApp: React.FC = () => {
  const { isInitializing } = useApp();

  usePageTracking();

  // Show loading while initializing
  if (isInitializing) {
    return <LoadingOverlay visible loadingText="Initializing..." />;
  }

  // Public auth pages - skip all providers
  if (isPublicAuthPage()) {
    return <PublicAppContent />;
  }

  // Authenticated pages - providers are inside AuthenticatedRouterWrapper
  return <AuthenticatedAppContent />;
};

function App() {
  return (
    <ThemeProvider>
      <Favicon />

      <AppProvider>
        <GoogleMapsProvider>
          <LocationHistoryProvider>
            <AuthenticatedApp />
          </LocationHistoryProvider>
        </GoogleMapsProvider>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;